import React, { useCallback } from "react"
import { AppDetailBase } from "../AppDetailBase/AppDetailBase"
import { useDispatch } from "react-redux"
import { push } from "connected-react-router"
import { routePaths } from "../../../../../../Routes"
import { getValue } from "../../../../../../Models/Client/FetchState"
import { FilterAndSortExpression } from "../../../../../../Models/Client/FilterAndSortExpression"
import { SortExpression } from "../../../../../../Models/Client/SortExpression"
import { MainThunkDispatch } from "../../../../../../Reducers"
import { SuperAILabelerTable } from "../../../Common/CollaboratorTable/SuperAILabelerTable"
import {
    SuperAILabelerModel,
    SuperAILabelerParams
} from "../../../../../../Models/Client/SuperAILabelerModel"
import { useSuperAILabelers } from "../../../../../../Hooks/useSuperAILabelers"
import { FilterExpression } from "../../../../../../Models/Client/FilterExpression"
import { measurer } from "neql"
import { useNeqlContextProvider } from "../../../../../../Hooks/useNeqlContextProvider"
import {
    failMessage,
    successMessage
} from "../../../../../../Actions/Client/MessagesActions"

interface SuperAILabelersProps {
    orgUsername: string
    appID: string
    params: SuperAILabelerParams
    filterAndSort: FilterAndSortExpression<SuperAILabelerModel>
}

const PAGE_SIZE = 30

export const SuperAILabelers: React.FC<SuperAILabelersProps> = ({
    orgUsername,
    appID,
    params,
    filterAndSort
}: SuperAILabelersProps) => {
    const dispatch: MainThunkDispatch = useDispatch<MainThunkDispatch>()
    const [labelersFetch, loadMore, refresh] = useSuperAILabelers(
        appID,
        PAGE_SIZE,
        params
    )
    const client = useNeqlContextProvider("measurer")
    const [updateActiveness] = measurer.useUpdateLabelerStatusMutation({
        client
    })
    const handleToggleActive = useCallback(
        async (ids: string[], active: boolean) => {
            const success = `${
                ids.length > 1 ? "Workers are" : "Worker is"
            } updated`
            const fail = `Could not update ${
                ids.length > 1 ? "workers" : "worker"
            }`

            try {
                await updateActiveness({
                    variables: { appId: appID, labelerIds: ids, active }
                })
                dispatch(successMessage(success))
                await refresh()
            } catch (e) {
                dispatch(failMessage(fail, e))
            }
        },
        [appID, dispatch, refresh, updateActiveness]
    )
    const handleChangeSort = useCallback(
        (s: SortExpression<SuperAILabelerModel>) =>
            dispatch(
                push(
                    routePaths.appSuperAILabelers(orgUsername, appID, {
                        ...filterAndSort,
                        sort: s
                    })
                )
            ),
        [appID, dispatch, filterAndSort, orgUsername]
    )
    const handleChangeFilter = useCallback(
        (newFilters: FilterExpression<SuperAILabelerModel>[]) =>
            dispatch(
                push(
                    routePaths.appSuperAILabelers(orgUsername, appID, {
                        sort: filterAndSort.sort,
                        filter: newFilters
                    })
                )
            ),
        [appID, dispatch, filterAndSort.sort, orgUsername]
    )

    return (
        <AppDetailBase
            orgUsername={orgUsername}
            id={appID}
            title="Super.AI Workers"
            breadcrumbs={["Workers"]}
            fixedContentWidth={false}
        >
            <SuperAILabelerTable
                items={getValue(labelersFetch)?.items}
                loadMore={loadMore}
                totalCount={getValue(labelersFetch)?.total ?? 0}
                filteredCount={getValue(labelersFetch)?.count ?? 0}
                toggleActive={handleToggleActive}
                sort={filterAndSort.sort}
                filters={filterAndSort.filter}
                onChangeSort={handleChangeSort}
                onChangeFilter={handleChangeFilter}
            />
        </AppDetailBase>
    )
}
